import { removeSelectedTest, getTestNode, getAllDescendants, addSelectedTest } from "./testSelection.js";
import { removeItem } from "./index.js";
import { removeSelectedDiscountPanel } from "./discountPanelSelection.js";
import { BLOOD_SALIVA_ADDON_IDS } from "./geneticAddonConstants.js";

export function handleException(selectProp, notAllowedProp, supportObj) {
  let { exceptionMap } = supportObj;
  additionEnableException(exceptionMap.ADDITION_ENABLE, selectProp, notAllowedProp, supportObj);
  delinkDisableException(exceptionMap.DELINK_DISABLE, selectProp, notAllowedProp, supportObj);
  linkDisableException(exceptionMap.LINK_DISABLE, selectProp, notAllowedProp, supportObj);
}

// exceptionTest is only selectable when at least one test in affectTestList is selected
// if no test in affectedTestList is selected, 
// remove exceptionTest from selected list if any and let exceptionTest be not selectable
function additionEnableException(exceptionList, selectProp, notAllowedProp, supportObj) {
  if (!exceptionList) {
    return;
  }
  let { testNodeList, bundleList, selectedTestIdList } = supportObj;
  exceptionList.forEach(exception => {
    let exceptionNode = testNodeList.find(node => node.id === exception.itemId);
    if (!exceptionNode) {
      return;
    }
    let affectTestList = testNodeList
      .filter(node => exception.affectedTestIdList.includes(node.id))
      .map(node => node.value);
    let affectBundleList = bundleList
      .filter(b => exception.affectedBundleIdList.includes(b.id));
    let affectList = affectTestList.concat(affectBundleList);
    if (affectList.some(item => item[selectProp])) {
      // some of affect tests/bundles are selected
      exceptionNode.value[notAllowedProp] = false;
    }
    else { // no affected tests are selected
      // de-select and remove exception test if any
      removeItem(exceptionNode.id, selectedTestIdList);
      exceptionNode.value[selectProp] = false;
      // set exception test be not selectable
      exceptionNode.value[notAllowedProp] = true;
    }
  })
}

// when exception test is selected, no tests in affectTestList can be selectable
function delinkDisableException(exceptionList, selectProp, notAllowedProp, supportObj) {
  if (!exceptionList) {
    return;
  }
  let { testNodeList, bundleList } = supportObj;
  exceptionList.forEach(exception => {
    let exceptionNode = testNodeList.find(node => node.id === exception.itemId);
    if (!exceptionNode) {
      return;
    }
    let affectTestNodeList = testNodeList
        .filter(node => exception.affectedTestIdList.includes(node.id));
    let affectBundleList = bundleList
      .filter(b => exception.affectedBundleIdList.includes(b.id));
    let descendants = [];
    affectTestNodeList.forEach(node => {
      getAllDescendants(node, descendants);
    });
    // add albumin(id: 371_23) to affectTestNodeList (if not in the list) if testo free is in the list
    affectTestNodeList = affectTestNodeList.concat(descendants);
    let albuminNode = affectTestNodeList.find(node => node.id === "371_23");
    let testoFreeNode = affectTestNodeList.find(node => node.id === 825);
    if (testoFreeNode && !albuminNode) {
      albuminNode = testNodeList.find(node => node.id === "371_23");
      affectTestNodeList.push(albuminNode);
    }
    if (exceptionNode.value[selectProp]) {
      // exception test is selected, remove all affect tests and bundles and 
      // set them be not selectable
      affectTestNodeList.forEach(testNode => {
        testNode.value[notAllowedProp] = true;
        removeSelectedTest(testNode.id, selectProp, supportObj);
      });
      affectBundleList.forEach(bundle => {
        bundle[notAllowedProp] = true;
        removeSelectedDiscountPanel(bundle.bundleId, selectProp. supportObj);
      });
    }
    else {
      let remainingExceptions = exceptionList.filter(e => e.itemId !== exception.itemId);
      // exception test is not selected, check whether any of its affected items is selectable
      affectTestNodeList.map(node => node.value).concat(affectBundleList).forEach(item => {
        // find whether remaining exceptions contain this affected item
        let remainingExceptionIds = remainingExceptions.filter(e => {
          let { affectedTestIdList, affectedBundleIdList } = e;
          return affectedTestIdList.concat(affectedBundleIdList).includes(item.id);
        }).map(e => e.itemId);
        // if any of other exceptions is selected, this item is not selectable
        // otherwise, this item is selectable
        let remainingExceptionNodes = testNodeList.filter(node => remainingExceptionIds.includes(node.id));
        if (remainingExceptionNodes.some(node => node.value[selectProp])) {
          item[notAllowedProp] = true;
        } else {
          item[notAllowedProp] = false;
        }
      });
    }
  })
}

// when exception test is selected, affect tests must also be selected
// and affect tests cannot be de-select solely
function linkDisableException(exceptionList, selectProp, notAllowedProp, supportObj) {
  if (!exceptionList) {
    return;
  }
  let { testNodeList, bundleList, selectedTestIdList } = supportObj;
  exceptionList.forEach(exception => {
    let exceptionNode = testNodeList.find(node => node.id === exception.itemId);
    if (!exceptionNode) {
      return;
    }
    let affectTestList = [];
    exception.affectedTestIdList.forEach(id => {
      let testNode = getTestNode(testNodeList, id);
      if (testNode) {
        affectTestList.push(testNode.value);
      }
    });
    let affectBundleList = bundleList
      .filter(b => exception.affectedBundleIdList.includes(b.id));
    if (exceptionNode.value[selectProp]) {
      // exception test is selected, so select affect tests
      affectTestList.concat(affectBundleList).forEach(item => {
        if (!item[selectProp]) {
          // item is not selected earlier, or bundle/testgroup containing item is not selected
          // so need add to selected test
          selectedTestIdList.push(item.id);
        }
        item[selectProp] = true;
        item[notAllowedProp] = true;
      });
    }
    else { 
      // exception test is selectable but not selected, so let all affect tests be selectable
      // except for total immunoglobulins (id: 167)
      affectTestList.concat(affectBundleList).filter(item => !item[notAllowedProp]).forEach(item => {
        if (item.id !== 167) {
          item[notAllowedProp] = false;
        }
      });
    }
    // special case for Testo, Free [Incl. Total, Albumin, SHBG]
    // if this test is not select, check albumin(id: 371_23)
    // if it is selected solely or belongs to other selected bundles (is selectable), skip
    if (exceptionNode.id === 825) {
      // remove albumin(id: 371_23) from selected list if any and let it be selectable
      let albuminNode = testNodeList.find(node => node.id === "371_23");
      if (!exceptionNode.value[selectProp]) {
        if (albuminNode.value[notAllowedProp]) {
          // albumin is not selected solely or belong to other selected bundles
          // let albumin be selectable
          // and remove it from selected test if any
          removeItem(albuminNode.id, selectedTestIdList);
          albuminNode.value[notAllowedProp] = false;
          albuminNode.value[selectProp] = false;
        }
        // if the albumin's parent is selected, make sure the albumin is also selectd
        if (albuminNode.parent.value[selectProp]) {
          albuminNode.value[selectProp] = true;
        }
      }
      // if testo free is not selectable, then albumin must be not selectable
      if (exceptionNode.value[notAllowedProp]) {
        albuminNode.value[notAllowedProp] = true;
      }
    }
  });

  // Special case for Total Immunoglobulin (id: 167):
  // if none of the exceptions that affect it are selected, de-select 167 as well
  const immunoglobulinId = 167;
  const immunoglobulinNode = getTestNode(testNodeList, immunoglobulinId);
  if (immunoglobulinNode) {
    const affectingExceptions = exceptionList.filter(e => e.affectedTestIdList.includes(immunoglobulinId));
    if (affectingExceptions.length > 0) {
      const anyExceptionSelected = affectingExceptions.some(e => {
        const exNode = testNodeList.find(node => node.id === e.itemId);
        return exNode && exNode.value[selectProp];
      });
      if (!anyExceptionSelected) {
        removeItem(immunoglobulinNode.id, selectedTestIdList);
        immunoglobulinNode.value[selectProp] = false;
      }
    }
  }
}

// when exception test is selected, all affect tests are also selected - finished in addSelectedTest method 
// and vise versa - need to do here
// when one of affect tests is de-selected, de-select exception test but keep others - finished in removeSelectedTest method
// linkEnableException({commit, getters, state}, {exception, currentTest}) {
//   let exceptionTest = getters.testList.find(test => test.id === exception.itemId);
//   let affectTestIdList = exception.affectedItemIdList.map(idObj => idObj.affectedItemId);
//   // if all affect tests are selected, then remove them from selectedTestList and
//   // add the exception test
//   if (affectTestIdList.every(id => state.selectedTestIdList.includes(id))) {
//     affectTestIdList.forEach(id => commit("REMOVE_SELECTED_TEST_ID", id));
//     exceptionTest.isSelect = true;
//     commit("ADD_SELECTED_TEST_ID", exception.itemId);
//   }
// }

/**
 * Special rule for blood/saliva addons: ApoE, MTHFR, and FACTOR II-V
 * These tests have two versions: blood and saliva.
 * - If both eligible blood and saliva tests are selected, only blood version is selectable
 * - and replace saliva version with blood version if saliva version is selected
 */
export function handleBloodSalivaAddonException(selectProp, notAllowedProp, supportObj) {
  let { testNodeList } = supportObj;
  if (!testNodeList) {
    return;
  }

  // For each addon group, check if both blood and saliva versions are selectable
  Object.values(BLOOD_SALIVA_ADDON_IDS).forEach(({ blood: bloodId, saliva: salivaId }) => {
    let bloodNode = getTestNode(testNodeList, bloodId);
    let salivaNode = getTestNode(testNodeList, salivaId);

    if (!bloodNode || !bloodNode.value || !salivaNode || !salivaNode.value) {
      return;
    }

    let bloodIsSelectable = bloodNode.value[notAllowedProp] === false;
    let salivaIsSelectable = salivaNode.value[notAllowedProp] === false;

    // If both versions are selectable, make only blood version selectable
    if (bloodIsSelectable && salivaIsSelectable) {
      salivaNode.value[notAllowedProp] = true;
      
      // If saliva version is selected, replace it with blood version
      if (salivaNode.value[selectProp] === true) {
        removeSelectedTest(salivaId, selectProp, supportObj);
        addSelectedTest(bloodId, selectProp, supportObj);
      }
    }
  });
}